import { useEffect, useMemo } from "react";
import { Box, VStack, Skeleton } from "@chakra-ui/react";
import CDS from "design-system";
import { useTasksQueryByIds } from "features/Resources/Task/hooks";
import type { Task, TaskSummary } from "features/Resources/Task/models";
import { computeTaskStats } from "features/Resources/Task/helpers/stats";
import { TaskCard } from "features/Resources/Task/Components/UserTaskSummary";
import useSideDrawer from "hooks/useSideDrawer";
import ActionSideDrawer from "components/ActionSideDrawer";

export interface CheckpointTaskStats {
  tasksComplete: number;
  tasksTotal: number;
  tasksOverdue: number;
  followUpCount: number;
}

interface CheckpointTasksSectionProps {
  data: {
    results?: { tasks?: { id: number }[] };
    timezone?: string;
  };
  onFollowUpCount?: (count: number) => void;
  onStatsChange?: (stats: CheckpointTaskStats) => void;
}

export default function CheckpointTasksSection({ data, onFollowUpCount, onStatsChange }: CheckpointTasksSectionProps) {
  const snapshotTasks = useMemo(() => data.results?.tasks || [], [data.results?.tasks]);
  const taskIds = useMemo(() => snapshotTasks.map((t) => String(t.id)), [snapshotTasks]);
  const taskQueries = useTasksQueryByIds(taskIds, { enabled: taskIds.length > 0 });
  const isLoading = taskQueries.some((q) => q.isLoading);

  const resolvedTasks: Task[] = useMemo(() => {
    return snapshotTasks.map((_snapshot, index) => taskQueries[index]?.data).filter((t): t is Task => t != null);
  }, [snapshotTasks, taskQueries]);

  // Compute live stats from resolved tasks and report to parent
  useEffect(() => {
    const stats = computeTaskStats(resolvedTasks);

    onFollowUpCount?.(stats.openAssigneeCount);
    onStatsChange?.({
      tasksComplete: stats.completedCount,
      tasksTotal: resolvedTasks.length,
      tasksOverdue: stats.overdueCount,
      followUpCount: stats.openAssigneeCount,
    });
  }, [resolvedTasks, onFollowUpCount, onStatsChange]);

  const { isOpen, onClose, selectedItem, expandClickHandlerByItem } = useSideDrawer<TaskSummary>({
    getItemId: (item) => item?.id,
  });

  return (
    <Box>
      <CDS.Text10
        fontSize="2xs"
        fontWeight="600"
        color="content.tertiary"
        textTransform="uppercase"
        letterSpacing="wider"
        mb="2.5"
      >
        Tasks ({snapshotTasks.length})
      </CDS.Text10>

      {isLoading ? (
        <VStack spacing={2} align="stretch">
          {snapshotTasks.map((t) => (
            <Skeleton key={t.id} height="44px" borderRadius="md" />
          ))}
        </VStack>
      ) : resolvedTasks.length > 0 ? (
        <VStack spacing={0} align="stretch">
          {resolvedTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={(t) => expandClickHandlerByItem(t)}
              showAssignee
              compact
            />
          ))}
        </VStack>
      ) : (
        <CDS.Text12 color="content.tertiary">
          No tasks recorded
        </CDS.Text12>
      )}

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedItem?.prefixId} />
    </Box>
  );
}
